<template>
  <component :is="rootComponent" class="btn" :class="classes" :disabled="props.disabled || props.loading" :to="props.to">
    <etg-loading v-if="props.loading" :center="true" :class="[props.size === 'small' ? 'size-5' : 'size-6']" />
    <template v-if="props.icon">
      <svg-icon :name="props.icon" class="flex-shrink-0" :class="svgClasses" :color="props.iconColor" />
    </template>
    <span v-if="props.type !== 'icon'" class="etg-btn-text" :class="textClasses"><slot>Button</slot></span>
  </component>
</template>

<script setup lang="ts">
import { twMerge } from 'tailwind-merge'

import LocaleLink from '@/components/global/locale-link.vue'
import EtgLoading from '@/components/common/etg-loading.vue'

import type { EtgButtonProps } from '@/types/component'
import { BUTTON_SIZE_CLASSES, BUTTON_SVG_SIZE_CLASSES, buttonVariants } from '@/constants/button'

const props = withDefaults(defineProps<EtgButtonProps>(), {
  type: 'default',
  size: 'medium',
  iconPosition: 'left',
  iconColor: 'black',
  center: true,
  block: false,
  bordered: false,
  loading: false,
  truncateText: false,
  allowWrap: false,
})

const rootComponent = computed(() => (typeof props.to === 'undefined' ? 'button' : LocaleLink))
const btnVariantClasses = computed(() => buttonVariants({ type: props.type, size: props.size }))
const classes = computed(() => {
  return twMerge(
    `btn-${props.type}`,
    BUTTON_SIZE_CLASSES[props.size],
    btnVariantClasses.value,
    props.icon && props.iconPosition === 'right' && 'flex-row-reverse',
    props.center && 'justify-center',
    props.block && 'w-full',
    props.bordered && 'ring-1',
    props.loading && 'btn-loading',
    props.class
  )
})
const svgClasses = computed(() => {
  return [
    `svg-icon-${props.icon}`,
    BUTTON_SVG_SIZE_CLASSES[props.size] || BUTTON_SVG_SIZE_CLASSES.default,
    { 'opacity-0': props.loading },
    { 'flex-0': props.type !== 'icon' },
  ]
})

const lineClampClasses = computed(() => {
  if (props.allowWrap) return ['whitespace-normal', 'break-words', 'min-w-0']
  if (props.truncateText) return ['whitespace-normal', 'line-clamp-1']
  return ['whitespace-nowrap']
})

const textClasses = computed(() => [{ 'opacity-0': props.loading }, props.allowWrap ? 'flex-1' : 'flex-0', lineClampClasses.value, props.textClass])
</script>
